import { useAuth } from "@/context/AuthContext";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalItems } from "@/components/GlobalSwitcher";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// One dropdown for every view: a facility (the left nav is about that gym), or a cross-gym
// view (Lead Nurture, Global Admin, later Accounts) listed under "Across every gym".
export function FacilitySwitcher() {
  const { activeFacility, facilities, switchFacility, viewOnly } = useAuth();
  const globals = useGlobalItems();
  const loc = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const activeGlobal = globals.find((g) => loc.pathname.startsWith(g.to));
  const list = facilities || [];
  const active = activeFacility || list[0] || null;

  if (!active) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-[#262626] bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-400" data-testid="facility-switcher-empty">
        <Building2 className="h-3.5 w-3.5 text-zinc-500" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">No facility · check settings</span>
      </div>
    );
  }

  const canOpen = list.length > 1 || globals.length > 0;

  const pick = async (id) => {
    if (id === active?.id) { if (activeGlobal) navigate("/"); return; }
    setBusy(true);
    try {
      await switchFacility(id);
      const target = list.find((f) => f.id === id);
      toast.success(`Switched to ${target?.name || "facility"} · ${target?.currency_symbol}`);
      if (activeGlobal) navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Facility switch failed");
    } finally { setBusy(false); }
  };

  const badge = (
    <div className="flex flex-col text-left leading-tight">
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{activeGlobal ? "View" : "Facility"}</span>
      <span className="font-medium">{activeGlobal ? activeGlobal.label : (active?.name || "—")}</span>
    </div>
  );
  const currencyBadge = (
    <span className="ml-1 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 font-mono text-[10px]">
      {active?.currency_code || "INR"}
    </span>
  );

  if (!canOpen) {
    return (
      <div
        className="inline-flex items-center gap-2 rounded-full border border-[#262626] bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-200"
        title="Only one facility — add another from Global Admin to enable switching."
        data-testid="facility-switcher-single"
      >
        <Building2 className="h-3.5 w-3.5 text-blue-400" />
        {badge}
        {currencyBadge}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border border-[#262626] bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-200 hover:border-blue-500/60 hover:text-zinc-50 transition-colors disabled:opacity-50"
        data-testid="facility-switcher-trigger"
      >
        <Building2 className="h-3.5 w-3.5 text-blue-400" />
        {badge}
        {!activeGlobal && currencyBadge}
        {!activeGlobal && viewOnly && (
          <span className="rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid="view-only-badge">View only</span>
        )}
        <ChevronDown className="h-3 w-3 text-zinc-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0f0f0f] border-[#262626] text-zinc-100 min-w-[240px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          Facilities ({list.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#1f1f1f]" />
        {list.map((f) => (
          <DropdownMenuItem
            key={f.id}
            onClick={() => pick(f.id)}
            className="focus:bg-[#1a1a1a] cursor-pointer"
            data-testid={`facility-switcher-option-${f.id}`}
          >
            <div className="flex-1">
              <div className="text-sm font-medium">{f.name}</div>
              <div className="text-[10px] text-zinc-500">{f.country} · {f.currency_code}</div>
            </div>
            {f.id === active?.id && !activeGlobal && <Check className="h-3.5 w-3.5 text-emerald-400" />}
          </DropdownMenuItem>
        ))}
        {globals.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-[#1f1f1f]" />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Across every gym</DropdownMenuLabel>
            {globals.map(({ to, label, hint, icon: Icon, testid }) => (
              <DropdownMenuItem key={to} onClick={() => navigate(to)} className="focus:bg-[#1a1a1a] cursor-pointer" data-testid={testid}>
                <Icon className="h-3.5 w-3.5 mr-2 text-amber-300" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-[10px] text-zinc-500">{hint}</div>
                </div>
                {activeGlobal?.to === to && <Check className="h-3.5 w-3.5 text-emerald-400" />}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
